import React, { useState } from 'react';
import AddFamilyMemberModal from './AddFamilyMemberModal';
import { useFamilyRelationships } from '../../hooks/useFamilyRelationships';

const FamilyTreeBuilder = ({ currentUserId, familyMembers, onFamilyUpdate }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null,
    existingPersonId: null,
    parent1Id: null,
    parent2Id: null
  });

  const { connectExistingPeople, loading, error } = useFamilyRelationships();

  // Find spouse and parents for current user
  const currentUserSpouse = familyMembers.find(member =>
    member.relationship_to_user === 'spouse'
  );

  const currentUserParents = familyMembers.filter(member =>
    member.relationship_to_user === 'parent' ||
    member.relationship_to_user === 'father' ||
    member.relationship_to_user === 'mother'
  );

  const openModal = (mode, existingPersonId = null, parent1Id = null, parent2Id = null) => {
    setModalState({
      isOpen: true,
      mode,
      existingPersonId,
      parent1Id,
      parent2Id
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      mode: null,
      existingPersonId: null,
      parent1Id: null,
      parent2Id: null
    });
  };

  const handleFamilyMemberAdded = (result) => {

    // Update family tree data
    if (onFamilyUpdate) {
      onFamilyUpdate(result);
    }

    // Show success message
    const memberType = result.usage_pattern === 'two_parents_create_child' ? 'child' : result.relationship_type;
    alert(`Successfully added ${result.new_person?.full_name || result.new_person?.first_name} as ${memberType}!`);
  };

  const handleConnectExisting = async (person1Id, person2Id, relationshipType) => {
    try {
      const result = await connectExistingPeople(person1Id, person2Id, relationshipType);

      if (onFamilyUpdate) {
        onFamilyUpdate(result);
      }

      alert(`Successfully connected as ${relationshipType}!`);
    } catch (err) {
      alert('Failed to connect people: ' + err.message);
    }
  };

  return (
    <div className="family-tree-builder">
      <h3>Add Family Members</h3>

      {error && <div className="error-message">{error}</div>}

      <div className="action-buttons">
        {/* Pattern 1: One UUID → Create Second Person */}
        <button 
          onClick={() => openModal('spouse', currentUserId)}
          disabled={loading || currentUserSpouse}
          className="add-spouse-btn"
        >
          {currentUserSpouse ? 'Spouse Added' : 'Add Spouse'}
        </button>

        <button 
          onClick={() => openModal('parent', currentUserId)}
          disabled={loading}
          className="add-parent-btn"
        >
          Add Parent
        </button>

        <button 
          onClick={() => openModal('child', currentUserId)}
          disabled={loading}
          className="add-child-btn"
        >
          Add Child (Single Parent)
        </button>

        {/* Pattern 2: Two UUIDs → Create Third Person */}
        {currentUserSpouse && (
          <button 
            onClick={() => openModal('child_both_parents', null, currentUserId, currentUserSpouse.id)}
            disabled={loading}
            className="add-child-both-btn"
          >
            Add Child (Both Parents)
          </button>
        )}

        {/* Pattern 3: Connect existing people */}
        <div className="connect-existing-section">
          <h4>Connect Existing People</h4>
          <select 
            onChange={(e) => {
              const [person1Id, person2Id, relType] = e.target.value.split('|');
              if (person1Id && person2Id && relType) {
                handleConnectExisting(person1Id, person2Id, relType);
                e.target.value = '';
              }
            }}
            disabled={loading}
          >
            <option value="">Select relationship to create...</option>
            {familyMembers.length > 1 && familyMembers.map(member => (
              <option 
                key={`sibling-${member.id}`} 
                value={`${currentUserId}|${member.id}|sibling`}
              >
                Mark {member.name || member.first_name} as Sibling
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add Family Member Modal */}
      <AddFamilyMemberModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        mode={modalState.mode}
        existingPersonId={modalState.existingPersonId}
        parent1Id={modalState.parent1Id}
        parent2Id={modalState.parent2Id}
        onSuccess={handleFamilyMemberAdded}
      />
    </div>
  );
};

export default FamilyTreeBuilder;